<template>
  <div class="iabf-dancer-theme register-page">
    <div class="register-col">
      <template v-if="!showFestivalSoldOut">
      <h1 class="iabf-display">Register for IABF {{ registrationYear }}</h1>
      <p class="iabf-hint">
        We're so glad you're here. Let's have a great time!
      </p>
      </template>

      <div v-if="accessLoading" class="iabf-stack">
        <p class="iabf-hint">Checking registration status…</p>
      </div>

      <div v-else-if="registrationBlocked" class="iabf-card closed-card">
        <template v-if="accessMode === 'closed'">
          <h2 class="iabf-question">Registration opens August 30</h2>
          <p class="iabf-body">
            At the end of our 2026 Watch Party (from 12pm – approx 3pm Eastern).
            We’ll be so happy to see you. Spots will go quick, so
            <a
              :href="watchPartyZoomUrl"
              target="_blank"
              rel="noopener noreferrer"
            >join us live</a>
            to be the first to know when it opens.
          </p>
        </template>
        <template v-else>
          <h2 class="iabf-question">{{ closedTitle }}</h2>
        </template>
        <p v-if="earlyWaitlisted" class="iabf-hint" style="margin-top: 12px">
          <template v-if="skipEmailStep">
            You’re signed in as {{ loggedInEmail }}. If you used a different email last year, sign out and try that one.
          </template>
          <template v-else>
            If you’ve been with us before, try the email you used last year.
          </template>
        </p>
        <p class="iabf-hint" style="margin-top: 12px">
          Questions?
          <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>
        </p>
        <button
          v-if="earlyWaitlisted"
          type="button"
          class="iabf-btn iabf-btn--ghost iabf-btn--block"
          style="margin-top: 16px"
          @click="retryEarlyEmail"
        >
          {{ skipEmailStep ? 'Sign out' : 'Try a different email' }}
        </button>
      </div>

      <template v-else>
      <div v-if="stripeTestMode && step === 6" class="early-banner" role="status">
        <strong>Stripe test mode.</strong>
        Use card 4242 4242 4242 4242 — no real charge.
      </div>

      <div v-if="waitlistOfferActive && !alreadyRegistered2027" class="early-banner" role="status">
        A spot opened for you. Finish registering by {{ waitlistOfferExpiresLabel }} to claim it.
      </div>

      <div v-if="accessMode === 'early' && step < 3 && !alreadyRegistered2027 && !showFestivalSoldOut" class="early-banner">
        Pre-registration is open for returning IABF dancers only.
        <template v-if="publicOpensLabel"> Public registration opens {{ publicOpensLabel }}.</template>
      </div>

      <div v-if="holdActive && !alreadyRegistered2027" class="hold-timer" :class="{ urgent: holdSecondsLeft <= 60 }">
        <strong>We’ve got your place:</strong> {{ roomLabel || room }}
        · <span class="hold-countdown">{{ holdCountdownLabel }}</span> left
      </div>

      <div
        v-if="step >= 3 && (welcomeBackLine || usingDepositCredit || emailRoomReservation)"
        class="recognize-banner"
        role="status"
      >
        <p v-if="welcomeBackLine" class="recognize-hello">{{ welcomeBackLine }}</p>
        <p v-if="usingDepositCredit">
          Your held deposit<template v-if="depositCreditFromYear"> from {{ depositCreditFromYear }}</template> covers today.
        </p>
        <p v-if="emailRoomReservation">
          We saved this room for you: {{ reservedRoomLabel }}
        </p>
      </div>

      <template v-if="!alreadyRegistered2027 && !showFestivalSoldOut">
      <div class="iabf-progress" aria-hidden="true">
        <span :style="{ width: progressPct + '%' }"></span>
      </div>
      <p class="iabf-caption step-label">Step {{ displayStep }} of {{ displayTotalSteps }} · {{ stepTitle }}</p>
      </template>

      <p v-if="error && !alreadyRegistered2027" class="error-banner" role="alert">{{ error }}</p>

      <!-- Festival sold out: waitlist instead of room picker -->
      <section v-if="step === 1 && showFestivalSoldOut" class="iabf-stack sold-out-waitlist">
        <h1 class="iabf-display">IABF {{ registrationYear }} is Sold Out</h1>
        <p class="iabf-body">
          This year’s spots are gone. If someone has to let a place go, we’ll offer space in the order emails arrive here.
        </p>

        <div v-if="waitlistJoined" class="iabf-card" role="status">
          <p class="iabf-body">
            {{ waitlistAlready
              ? 'You’re already on the list — we’ll be so glad if a place opens.'
              : 'You’re on the list. We’ll be so glad if a place opens.' }}
          </p>
        </div>
        <template v-else>
          <div class="iabf-field">
            <label class="iabf-label" for="reg-waitlist-email">Your email</label>
            <input
              id="reg-waitlist-email"
              v-model.trim="waitlistEmail"
              class="iabf-input"
              type="email"
              autocomplete="email"
              placeholder="you@example.com"
              :disabled="waitlistBusy"
              @keyup.enter="joinWaitlist"
            />
          </div>
          <p v-if="waitlistError" class="error-banner error-banner--cta" role="alert">{{ waitlistError }}</p>
          <button
            type="button"
            class="iabf-btn iabf-btn--primary iabf-btn--block"
            :disabled="waitlistBusy || !waitlistEmail"
            @click="joinWaitlist"
          >
            {{ waitlistBusy ? 'Saving…' : 'Join the waiting list' }}
          </button>
        </template>
        <p class="iabf-hint" style="margin-top: 16px">
          Questions? We’re here —
          <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>
        </p>
      </section>

      <!-- 1. Ticket / room (held for 5 minutes) -->
      <section v-else-if="step === 1" class="iabf-stack">
        <h2 class="iabf-question">Where would you like to stay?</h2>
        <p class="iabf-hint">
          We’ll hold this spot for 5 minutes while you finish.
        </p>

        <div
          v-if="holdExpiredBanner"
          class="just-taken-card hold-expired-card"
          role="status"
          @click="dismissHoldExpired"
        >
          <h3 class="just-taken-title">Time’s up</h3>
          <p class="just-taken-body">
            Your hold
            <template v-if="holdExpiredRoomLabel"> on {{ holdExpiredRoomLabel }}</template>
            ended. You’re welcome to choose a stay again.
          </p>
        </div>

        <div v-if="justTakenBanner" class="just-taken-card" role="alert">
          <h3 class="just-taken-title">That one just went</h3>
          <p class="just-taken-body">
            Someone else claimed the last
            <strong>{{ justTakenRoomLabel || 'spot' }}</strong>
            a moment ago. Take a look at what’s still open — we’d love to have you either way.
          </p>
          <button type="button" class="iabf-btn iabf-btn--secondary just-taken-dismiss" @click="justTakenBanner = false">
            Okay
          </button>
        </div>

        <div v-if="cottageFallbackOffer" class="just-taken-card cottage-fallback-card" role="status">
          <h3 class="just-taken-title">That cottage just went</h3>
          <p class="just-taken-body">
            We saved a dorm for you in case you’d like that instead.
          </p>
          <button type="button" class="iabf-btn iabf-btn--primary iabf-btn--block" @click="acceptCottageFallback">
            Yes, I’ll take the dorm →
          </button>
          <button type="button" class="iabf-btn iabf-btn--ghost iabf-btn--block" @click="dismissCottageFallback">
            I’ll choose something else
          </button>
        </div>

        <p v-if="roomsLoading" class="iabf-hint">Loading availability…</p>
        <p v-else-if="busy || holdInFlight" class="iabf-hint">Saving your place…</p>
        <div v-else class="iabf-choices">
          <button
            v-for="opt in roomChoices"
            :key="opt.value"
            type="button"
            class="iabf-choice"
            :class="{
              'is-selected': room === opt.value,
              'is-sold-out': opt.soldOut,
              'is-saved-for-you': opt.reservedForYou || opt.heldForYou
            }"
            :disabled="opt.soldOut || busy || holdReleasing"
            @click="selectRoom(opt)"
          >
            <span
              v-if="opt.reservedForYou || opt.heldForYou"
              class="saved-for-you-badge"
            >
              {{ opt.heldForYou ? 'We’ve got this for you' : 'Saved for you' }}
            </span>
            <span class="iabf-choice-main">
            <span class="iabf-choice-icon">{{ opt.icon }}</span>
            <span class="iabf-choice-body">
              <span class="iabf-choice-title">{{ opt.title }}</span>
              <span class="iabf-choice-sub">{{ opt.sub }}</span>
              <span class="iabf-choice-price">
                {{ opt.priceLabel }}
                <span class="iabf-choice-price-plan">{{ opt.pricePlan }}</span>
              </span>
              <span v-if="opt.soldOut" class="room-meta sold">Sold out</span>
            </span>
            </span>
          </button>
        </div>

        <div v-if="needsRoommateName" class="roommate-field iabf-field">
          <label class="iabf-label" for="reg-roommate">Who are you staying with?</label>
          <input
            id="reg-roommate"
            v-model.trim="roommateName"
            class="iabf-input"
            type="text"
            autocomplete="name"
            placeholder="Their full name"
            :disabled="busy"
          />
          <p class="iabf-hint" style="margin-top: 6px">
            They have the room — you’re joining them.
          </p>
        </div>

        <button
          v-if="needsRoommateName"
          type="button"
          class="iabf-btn iabf-btn--primary iabf-btn--block"
          :disabled="busy || !room || roomIsSoldOut || !roommateNameReady"
          @click="continueFromRoom"
        >
          {{ (busy || holdInFlight) ? 'Saving your place…' : 'Save my place →' }}
        </button>
        <button
          v-else-if="holdReadyForSelection && !cottageFallbackOffer"
          type="button"
          class="iabf-btn iabf-btn--primary iabf-btn--block"
          :disabled="busy || holdInFlight"
          @click="continueFromRoom"
        >
          Continue with this stay →
        </button>
      </section>

      <!-- 2. Email -->
      <section v-else-if="step === 2" class="iabf-stack">
        <template v-if="alreadyRegistered2027">
          <h2 class="iabf-question">You’re already in</h2>
          <p class="iabf-hint">
            {{ email }} is registered for IABF {{ registrationYear }}.
            <template v-if="hasAuth">Log in with your Broche Ballet On-Demand password.</template>
            <template v-else>Set a password to open your dashboard.</template>
          </p>
          <div class="iabf-field">
            <label class="iabf-label" for="reg-login-password">{{ hasAuth ? 'Broche Ballet On-Demand password' : 'Create a password' }}</label>
            <input
              id="reg-login-password"
              v-model="loginPassword"
              class="iabf-input"
              type="password"
              autocomplete="current-password"
              @keyup.enter="loginAlreadyRegistered"
            />
          </div>
          <div v-if="!hasAuth" class="iabf-field">
            <label class="iabf-label" for="reg-login-confirm">Confirm password</label>
            <input
              id="reg-login-confirm"
              v-model="loginPasswordConfirm"
              class="iabf-input"
              type="password"
              autocomplete="new-password"
              @keyup.enter="loginAlreadyRegistered"
            />
          </div>
          <p v-if="loginError" class="error-banner error-banner--cta" role="alert">{{ loginError }}</p>
          <p v-if="resetSent" class="iabf-hint" role="status">Password reset email sent. Check your inbox.</p>
          <button
            type="button"
            class="iabf-btn iabf-btn--primary iabf-btn--block"
            :disabled="busy || !loginPassword"
            @click="loginAlreadyRegistered"
          >
            {{ busy ? 'Logging in…' : 'Log in to your dashboard' }}
          </button>
          <button
            v-if="hasAuth"
            type="button"
            class="iabf-btn iabf-btn--ghost iabf-btn--block"
            :disabled="busy"
            @click="resetAlreadyRegisteredPassword"
          >
            Forgot your password?
          </button>
          <button
            type="button"
            class="iabf-btn iabf-btn--ghost iabf-btn--block"
            :disabled="busy"
            @click="clearAlreadyRegistered"
          >
            Try a different email
          </button>
          <button
            type="button"
            class="iabf-btn iabf-btn--ghost iabf-btn--block"
            :disabled="busy"
            @click="goBackToRoom"
          >
            ← Back to room selection
          </button>
        </template>
        <template v-else>
        <h2 class="iabf-question">What’s your email?</h2>
        <p class="iabf-hint">We’ll send your confirmation here. If you’ve been with us before, we’ll recognize you.</p>
        <div class="iabf-field">
          <label class="iabf-label" for="reg-email">Email</label>
          <input
            id="reg-email"
            v-model.trim="email"
            class="iabf-input"
            type="email"
            autocomplete="email"
            placeholder="you@example.com"
            @keyup.enter="continueFromEmail"
          />
        </div>
        <p v-if="error" class="error-banner error-banner--cta" role="alert">{{ error }}</p>
        <button
          type="button"
          class="iabf-btn iabf-btn--primary iabf-btn--block"
          :disabled="busy"
          @click="continueFromEmail"
        >
          {{ busy ? 'Checking…' : 'Continue →' }}
        </button>
        <button type="button" class="iabf-btn iabf-btn--ghost iabf-btn--block" :disabled="busy" @click="goBackToRoom">
          ← Back to room selection
        </button>
        </template>
      </section>

      <!-- 3. About you -->
      <section v-else-if="step === 3" class="iabf-stack">
        <template v-if="showReturningReview">
          <h2 class="iabf-question">Still you?</h2>
          <p class="iabf-hint">
            <template v-if="returningAddressLines.length">
              We have this from last year. Take a peek at your details and let us know if they are still current.
            </template>
            <template v-else>
              We recognize you from last year. We still need a mailing address
              for gifts and packets.
            </template>
          </p>
          <div class="review-card" role="region" aria-label="Details from last year">
            <p class="review-name">{{ firstName }} {{ lastName }}</p>
            <p v-if="phone" class="review-line">{{ formattedPhone }}</p>
            <p v-if="returningAddressLines.length" class="review-address">
              <span v-for="(line, i) in returningAddressLines" :key="i">{{ line }}<br></span>
            </p>
            <p v-else class="review-missing">No mailing address on file yet — please add it so we can send your welcome gift.</p>
          </div>
          <p v-if="error" class="error-banner error-banner--cta" aria-hidden="true">{{ error }}</p>
          <button type="button" class="iabf-btn iabf-btn--primary iabf-btn--block" @click="confirmReturningDetails">
            Yes, this is current →
          </button>
          <button type="button" class="iabf-btn iabf-btn--ghost iabf-btn--block" @click="returningEditing = true">
            I need to change something
          </button>
          <button type="button" class="iabf-btn iabf-btn--ghost iabf-btn--block" @click="backFromAbout">Back</button>
        </template>

        <template v-else>
        <h2 class="iabf-question">{{ welcomeBack ? 'Update your details' : 'Tell us a little about you' }}</h2>
        <p class="iabf-hint">
          We’ll use this at check-in. We never sell your information.
          <template v-if="skipEmailStep && email"> We’ll send your confirmation to {{ email }}.</template>
        </p>
        <div class="iabf-field">
          <label class="iabf-label" for="reg-first">First name</label>
          <input id="reg-first" v-model.trim="firstName" class="iabf-input" type="text" autocomplete="given-name" />
        </div>
        <div class="iabf-field">
          <label class="iabf-label" for="reg-last">Last name</label>
          <input id="reg-last" v-model.trim="lastName" class="iabf-input" type="text" autocomplete="family-name" />
        </div>
        <div class="iabf-field">
          <label class="iabf-label" for="reg-phone">Phone</label>
          <input id="reg-phone" v-model.trim="phone" class="iabf-input" type="tel" autocomplete="tel" placeholder="Include country code if outside the US" />
        </div>
        <div class="mailing-block" role="group" aria-labelledby="reg-mailing-heading">
          <div class="mailing-head">
            <h3 id="reg-mailing-heading" class="mailing-heading">Mailing address</h3>
            <p class="iabf-hint">We’ll send a little welcome gift here.</p>
          </div>
          <div class="iabf-field">
            <label class="iabf-label" for="reg-country">Country</label>
            <select
              id="reg-country"
              class="iabf-input"
              :value="countrySelectValue"
              autocomplete="country"
              @change="onCountryChange($event.target.value)"
            >
              <option value="" disabled>Choose a country</option>
              <option
                v-for="c in mailingCountrySelectList"
                :key="c.code"
                :value="c.code"
                :disabled="!!c.disabled"
              >{{ c.name }}</option>
            </select>
          </div>
          <template v-if="structuredMailing">
            <div class="iabf-field">
              <label class="iabf-label" for="reg-street">Street address</label>
              <input id="reg-street" v-model.trim="addressStreet" class="iabf-input" type="text" autocomplete="address-line1" />
            </div>
            <div class="iabf-field">
              <label class="iabf-label" for="reg-street2">Apt / suite <span class="optional-mark">(optional)</span></label>
              <input id="reg-street2" v-model.trim="addressStreet2" class="iabf-input" type="text" autocomplete="address-line2" />
            </div>
            <div class="iabf-field">
              <label class="iabf-label" for="reg-city">City</label>
              <input id="reg-city" v-model.trim="addressCity" class="iabf-input" type="text" autocomplete="address-level2" />
            </div>
            <div class="addr-row">
              <div class="iabf-field addr-state">
                <label class="iabf-label" for="reg-state">{{ mailingRegionLabel }}</label>
                <input id="reg-state" v-model.trim="addressState" class="iabf-input" type="text" autocomplete="address-level1" />
              </div>
              <div class="iabf-field addr-zip">
                <label class="iabf-label" for="reg-zip">{{ mailingPostalLabel }}</label>
                <input id="reg-zip" v-model.trim="addressZip" class="iabf-input" type="text" autocomplete="postal-code" />
              </div>
            </div>
          </template>
          <div v-else-if="addressCountry || addressStreet" class="iabf-field">
            <label class="iabf-label" for="reg-envelope">Address</label>
            <p class="iabf-hint envelope-hint">
              Write it the way you’d put it on an envelope — including postcode.
            </p>
            <textarea
              id="reg-envelope"
              v-model="addressStreet"
              class="iabf-textarea addr-envelope"
              rows="4"
              autocomplete="street-address"
            ></textarea>
          </div>
        </div>
        <p v-if="error" class="error-banner error-banner--cta" aria-hidden="true">{{ error }}</p>
        <button type="button" class="iabf-btn iabf-btn--primary iabf-btn--block" @click="continueFromAbout">Looks good →</button>
        <button type="button" class="iabf-btn iabf-btn--ghost iabf-btn--block" @click="backFromAbout">Back</button>
        </template>
      </section>

      <!-- 4. Payment plan -->
      <section v-else-if="step === 4" class="iabf-stack">
        <h2 class="iabf-question">Which payment plan do you prefer?</h2>
        <p class="iabf-hint">
          <template v-if="onlyPayInFullToday">
            Remaining balance of {{ remainingLabel }} is due today.
          </template>
          <template v-else-if="payRemainingTodayOption">
            Both start with today’s {{ depositLabel }} deposit. You can pay the remaining {{ remainingLabel }} today, or spread it across the 5ths we have left.
          </template>
          <template v-else>
            Both start with today’s {{ depositLabel }} deposit. The remaining {{ remainingLabel }} follows the plan you pick.
          </template>
        </p>
        <p v-if="!onlyPayInFullToday" class="iabf-hint">
          We’ll automatically charge the card you use today on the 5th of the month.
          Need a different card later? You can update it anytime from your dashboard.
        </p>
        <div class="iabf-choices">
          <button
            v-for="opt in paymentPlanOptions"
            :key="opt.value"
            type="button"
            class="iabf-choice"
            :class="{ 'is-selected': installmentCount === opt.value }"
            @click="installmentCount = opt.value"
          >
            <span class="iabf-choice-icon">{{ opt.icon == null ? opt.value : opt.icon }}</span>
            <span class="iabf-choice-body">
              <span class="iabf-choice-title">{{ planOptionTitle(opt) }}</span>
              <span class="iabf-choice-sub">{{ planOptionSub(opt) }}</span>
            </span>
          </button>
        </div>
        <p v-if="error" class="error-banner error-banner--cta" aria-hidden="true">{{ error }}</p>
        <button type="button" class="iabf-btn iabf-btn--primary iabf-btn--block" @click="continueFromPlan">Continue →</button>
        <button type="button" class="iabf-btn iabf-btn--ghost iabf-btn--block" @click="step = 3">Back</button>
      </section>

      <!-- 5. Terms -->
      <section v-else-if="step === 5" class="iabf-stack">
        <h2 class="iabf-question">A little fine print</h2>
        <p class="iabf-hint">
          We understand that life happens, and we strive to balance flexibility with the significant
          upfront costs of planning a world-class festival. Please review our full policy carefully
          before registering.
        </p>
        <div class="iabf-card terms-card">
          <p>
            Venue contracts, performance spaces, master faculty, and support staff are fully committed
            and paid for based on registration numbers. Because spots are limited, late cancellations
            prevent other dancers from attending and leave costs unrecoverable. Thank you for your
            understanding and support in making this event possible.
          </p>
          <h3>If you need to cancel</h3>
          <p>
            All cancellation terms apply universally, including cases of personal injury, illness,
            medical emergencies, work conflicts, or unforeseen circumstances.
          </p>
          <h3>Non-refundable deposit</h3>
          <p v-if="usingDepositCredit">
            The initial {{ depositLabel }} deposit is non-refundable under any circumstances.
            This deposit has already been transferred forward<template v-if="depositCreditFromYear"> from {{ depositCreditFromYear }}</template>
            and cannot be transferred forward again.
          </p>
          <p v-else>
            The initial {{ depositLabel }} deposit is non-refundable under any circumstances.
            This deposit may be transferred forward to the next festival. Space in the next festival
            is not guaranteed, and early registration is encouraged.
          </p>
          <h3>Non-transferable</h3>
          <p v-if="usingDepositCredit">
            Registrations and spots are non-transferable to other dancers or future event years.
          </p>
          <p v-else>
            Other than the eligibility to transfer a deposit forward one year, registrations and spots
            are non-transferable to other dancers or future event years.
          </p>
          <h3>Refund schedule</h3>
          <p>
            <strong>On or before {{ cancelByLabel }}.</strong>
            You are eligible for a 50% refund of the fee beyond the non-refundable {{ depositLabel }} deposit.
            Written notice must be received by {{ cancelByTimeLabel }}.
          </p>
          <p>
            <strong>After {{ cancelByLabel }}.</strong>
            No refunds or credits (partial or full) will be issued for any reason after {{ cancelByLabel }}.
          </p>
          <h3>Travel insurance</h3>
          <p>
            Because this policy is strictly enforced regardless of the reason for cancellation —
            including sudden injury or illness — we strongly recommend third-party travel insurance
            to protect your registration, flights, and accommodations. You can compare options and
            purchase coverage on your own through sites like
            <a href="https://www.squaremouth.com" target="_blank" rel="noopener noreferrer">SquareMouth</a>
            or
            <a href="https://www.insuremytrip.com" target="_blank" rel="noopener noreferrer">InsureMyTrip</a>.
          </p>
          <p class="terms-all-link">
            <a :href="policiesUrl" target="_blank" rel="noopener noreferrer">View all terms and conditions</a>
          </p>
        </div>
        <label class="terms-check">
          <input v-model="termsAccepted" type="checkbox" />
          <span>I understand and accept these terms</span>
        </label>
        <p v-if="error" class="error-banner error-banner--cta" aria-hidden="true">{{ error }}</p>
        <button
          type="button"
          class="iabf-btn iabf-btn--primary iabf-btn--block"
          :disabled="!termsAccepted"
          @click="continueFromTerms"
        >
          {{ usingDepositCredit ? 'Continue to confirm →' : 'Continue to deposit →' }}
        </button>
        <button type="button" class="iabf-btn iabf-btn--ghost iabf-btn--block" @click="step = 4">Back</button>
      </section>

      <!-- 6. Deposit / credit + card (stays on this page) -->
      <section v-else-if="step === 6" class="iabf-stack">
        <h2 class="iabf-question">You’re almost in</h2>
        <p class="iabf-hint">
          <template v-if="usingDepositCredit || cardSetupMode">
            Set up your card for Auto-Payments. $0 due today.
          </template>
          <template v-else>
            Pay your deposit here. We’ll use this card for the remaining payments. You can change it anytime on your dashboard after registration.
          </template>
        </p>
        <div class="iabf-card summary-card">
          <p class="iabf-choice-title">{{ firstName }} {{ lastName }}</p>
          <p v-if="welcomeBackLine" class="iabf-body">{{ welcomeBackLine }}</p>
          <p class="iabf-body">{{ email }} · {{ phone }}</p>
          <p v-if="addressSummary" class="iabf-body">{{ addressSummary }}</p>
          <p class="iabf-body" style="margin-top: 10px"><strong>Stay:</strong> {{ roomLabel }}</p>
          <p v-if="needsRoommateName && roommateName" class="iabf-body">
            <strong>Sharing with:</strong> {{ roommateName }}
          </p>
          <p class="iabf-body"><strong>Total:</strong> {{ packageTotalLabel }}</p>
          <p class="iabf-body"><strong>Plan:</strong> {{ planSummaryLabel }}</p>
          <p class="iabf-body" style="margin-top: 10px">
            <strong>Due now:</strong>
            <template v-if="usingDepositCredit || cardSetupMode">
              $0 — apply held {{ formatUsd(depositCreditCents) }} deposit<template v-if="depositCreditFromYear"> from {{ depositCreditFromYear }}</template>
            </template>
            <template v-else>
              {{ dueTodayLabel }}
            </template>
          </p>
        </div>

        <div class="reg-stripe-box">
          <div v-if="usingDepositCredit || cardSetupMode" class="reg-stripe-lead">
            <p class="reg-stripe-lead-title">Set up your card for Auto-Payments</p>
            <p class="reg-stripe-lead-body">$0 due today.</p>
          </div>
          <p v-if="payPreparing" class="iabf-hint" style="margin: 0">Getting the card form ready…</p>
          <div id="reg-card-element" :class="{ 'is-waiting': payPreparing }"></div>
        </div>
        <p v-if="stripeReady" class="iabf-hint" style="margin-top: 0">
          If your bank asks to confirm, you may hop away for a moment and come right back.
        </p>

        <p v-if="error" class="error-banner error-banner--cta" aria-hidden="true">{{ error }}</p>
        <button
          v-if="usingDepositCredit || cardSetupMode"
          type="button"
          class="iabf-btn iabf-btn--primary iabf-btn--block"
          :disabled="busy || payPreparing || !stripeReady"
          @click="saveSetupCard"
        >
          {{ busy ? 'Saving your card…' : 'Save card & finish' }}
        </button>
        <button
          v-else
          type="button"
          class="iabf-btn iabf-btn--primary iabf-btn--block"
          :disabled="busy || payPreparing || !stripeReady"
          @click="payDeposit"
        >
          {{ busy ? 'Confirming your payment…' : 'Pay ' + dueTodayLabel + ' to join' }}
        </button>
        <button
          type="button"
          class="iabf-btn iabf-btn--ghost iabf-btn--block"
          :disabled="busy"
          @click="step = 5"
        >
          Back
        </button>
      </section>
      </template>
    </div>
  </div>
</template>

<script>
import { formatPhoneDisplay } from '@/utils/phone'
import { ordinalYearLabel } from '@/utils/iabf-people'
import { auth, functions } from '@/firebase'
import { db } from '@/firebase'
import { iabfForYear } from '@/config/iabf-collections'
import {
  createRegistrationHold as apiCreateHold,
  releaseRegistrationHold as apiReleaseHold,
  lookupRegistrationEmail as apiLookupEmail,
  getRegistrationRoomAvailability as apiRoomAvailability
} from '@/utils/iabf-registration-api'
import {
  REGISTRATION_YEAR,
  DEPOSIT_CENTS,
  ROOM_OPTIONS,
  ONSITE_ROOM_LIMIT_KEYS,
  lodgingChoiceTitle,
  PAYMENT_PLAN_OPTIONS,
  CANCEL_POLICY,
  IABF_POLICIES_URL,
  REGISTRATION_HOLD_MS,
  roomRequiresRoommateName,
  festivalTotalCentsForRoom,
  remainingCentsForRoom,
  buildInstallmentAmountsUsd,
  remainingInstallmentFifths,
  isAfterFirstInstallment,
  resolveInstallmentPlan,
  formatUsdFromCents,
  depositUsd,
  installmentEachUsd,
  installmentEachMonthLabel
} from '@/config/iabf-registration-2027'
import {
  MAILING_COUNTRIES,
  mailingCountrySelectList,
  normalizeCountryCode,
  isStructuredMailingCountry,
  regionLabel,
  postalLabel,
  mailingAddressComplete,
  mailingAddressError,
  formatMailingLines,
  foldIntoEnvelope
} from '@/config/iabf-mailing-address'

const MAILING_COUNTRY_SELECT_LIST = mailingCountrySelectList()
const STORAGE_KEY = 'iabf-reg-2027-draft'
const SUCCESS_AUTH_KEY = 'iabf-reg-2027-success-auth'
const CF_PROD = 'https://us-central1-broche-ballet-online.cloudfunctions.net'
const CF_EMU = 'http://127.0.0.1:5001/broche-ballet-online/us-central1'
const STRIPE_JS_URL = 'https://js.stripe.com/v3/'

function isLocalStripeTest () {
  if (typeof window === 'undefined') {
    return false
  }
  if (!/localhost|127\.0\.0\.1/.test(window.location.hostname)) {
    return false
  }
  const q = window.location.search || ''
  if (/[?&]livestripe=1(?:&|$)/.test(q)) {
    try {
      localStorage.setItem('iabf-reg-stripe-test', '0')
    } catch (e) { /* ignore */ }
    return false
  }
  if (/[?&]teststripe=1(?:&|$)/.test(q)) {
    try {
      localStorage.setItem('iabf-reg-stripe-test', '1')
    } catch (e) { /* ignore */ }
    return true
  }
  try {
    if (localStorage.getItem('iabf-reg-stripe-test') === '0') {
      return false
    }
  } catch (e) { /* ignore */ }
  return true
}

function cfBase () {
  return isLocalStripeTest() ? CF_EMU : CF_PROD
}

function loadStripeJs () {
  if (typeof window !== 'undefined' && window.Stripe) {
    return Promise.resolve(window.Stripe)
  }
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-iabf-stripe]')
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Stripe))
      existing.addEventListener('error', reject)
      return
    }
    const s = document.createElement('script')
    s.src = STRIPE_JS_URL
    s.async = true
    s.setAttribute('data-iabf-stripe', '1')
    s.onload = () => resolve(window.Stripe)
    s.onerror = () => reject(new Error('Could not load card form'))
    document.head.appendChild(s)
  })
}

export default {
  name: 'IABFRegister',
  data () {
    return {
      step: 1,
      totalSteps: 6,
      busy: false,
      error: '',
      email: '',
      savedRoomEmail: '',
      savedRoomMiss: '',
      waitlistEmail: '',
      waitlistJoined: false,
      waitlistAlready: false,
      waitlistBusy: false,
      waitlistError: '',
      waitlistOffer: null,
      welcomeBack: false,
      returningYearNumber: 0,
      returningEditing: false,
      returningHadCompleteProfile: false,
      hasAuth: false,
      alreadyRegistered2027: false,
      loginPassword: '',
      loginPasswordConfirm: '',
      loginError: '',
      resetSent: false,
      firstName: '',
      lastName: '',
      phone: '',
      addressStreet: '',
      addressStreet2: '',
      addressCity: '',
      addressState: '',
      addressZip: '',
      addressCountry: '',
      mailingCountrySelectList: MAILING_COUNTRY_SELECT_LIST,
      room: '',
      roommateName: '',
      installmentCount: 6,
      termsAccepted: false,
      hasHeldDeposit: false,
      depositCreditCents: 0,
      depositCreditFromYear: null,
      roomOptions: ROOM_OPTIONS,
      roomAvailability: [],
      totalAvailability: null,
      roomsLoading: false,
      roomAvailabilityUnsub: null,
      justTakenBanner: false,
      holdExpiredBanner: false,
      holdExpiredRoom: '',
      holdExpiring: false,
      holdReleasing: false,
      holdExpiredTimer: null,
      justTakenRoom: '',
      emailRoomReservation: null,
      holdId: null,
      holdExpiresAt: null,
      holdSecondsLeft: 0,
      holdTimer: null,
      heldRoom: '',
      holdInFlight: false,
      pendingHoldRoom: null,
      cottageFallbackOffer: false,
      enrollmentId: null,
      payPreparing: false,
      stripeReady: false,
      cardSetupMode: false,
      stripeSetupCustomerId: '',
      registrationYear: REGISTRATION_YEAR,
      depositCents: DEPOSIT_CENTS,
      cancelByLabel: CANCEL_POLICY.cancelByLabel,
      cancelByTimeLabel: CANCEL_POLICY.cancelByTimeLabel,
      policiesUrl: IABF_POLICIES_URL,
      accessLoading: true,
      accessMode: 'closed', // public | early | closed — default closed until gate loads
      publicOpensAt: null,
      earlyAccessEligible: false,
      earlyWaitlisted: false,
      watchPartyZoomUrl: 'https://us02web.zoom.us/meeting/register/NWw_RDs4QLyafcfvTBqkCg'
    }
  },
  computed: {
    stripeTestMode () {
      return isLocalStripeTest()
    },
    registrationBlocked () {
      return this.accessMode === 'closed' || this.earlyWaitlisted
    },
    closedTitle () {
      if (this.accessMode === 'early' || this.earlyWaitlisted) {
        return this.publicOpensLabel
          ? 'Registration opens ' + this.publicOpensLabel
          : 'Registration opens soon'
      }
      return 'Registration is closed'
    },
    publicOpensLabel () {
      const iso = this.publicOpensAt
      if (!iso) {
        return ''
      }
      const parts = String(iso).slice(0, 10).split('-')
      if (parts.length !== 3) {
        return ''
      }
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      const m = parseInt(parts[1], 10)
      const day = parseInt(parts[2], 10)
      if (!m || !day) {
        return ''
      }
      return months[m - 1] + ' ' + day
    },
    loggedInEmail () {
      const dancer = this.$store.getters.currentIABFDancer
      const dancerEmail = dancer && String(dancer.email || '').trim().toLowerCase()
      const fromStore = this.$store.state.user && String(this.$store.state.user.email || '').trim().toLowerCase()
      const fromAuth = auth.currentUser && String(auth.currentUser.email || '').trim().toLowerCase()
      // Admin masquerade: register as the dancer on screen, not the admin login.
      if (this.$store.state.masqueradeDancerId && dancerEmail) {
        return dancerEmail
      }
      return fromStore || fromAuth || dancerEmail || ''
    },
    skipEmailStep () {
      if (this.waitlistOfferActive && this.email) {
        return true
      }
      return !!this.loggedInEmail
    },
    showSavedRoomUnlock () {
      if (this.skipEmailStep || this.emailRoomReservation) {
        return false
      }
      if (this.totalTicketsSoldOut) {
        return true
      }
      return this.roomChoices.some((c) => {
        return c.soldOut && ONSITE_ROOM_LIMIT_KEYS.indexOf(c.value) !== -1
      })
    },
    waitlistOfferActive () {
      const offer = this.waitlistOffer
      if (!offer || offer.expired || !offer.token) {
        return false
      }
      if (offer.expiresAt && Date.now() > Number(offer.expiresAt)) {
        return false
      }
      return true
    },
    waitlistOfferExpiresLabel () {
      const ms = this.waitlistOffer && Number(this.waitlistOffer.expiresAt)
      if (!ms) {
        return ''
      }
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }).format(new Date(ms))
    },
    showFestivalSoldOut () {
      if (this.waitlistOfferActive) {
        return false
      }
      return !!(
        this.totalTicketsSoldOut &&
        !this.roomsLoading &&
        !this.emailRoomReservation &&
        !this.holdActive
      )
    },
    displayTotalSteps () {
      return this.skipEmailStep ? this.totalSteps - 1 : this.totalSteps
    },
    displayStep () {
      if (!this.skipEmailStep) {
        return this.step
      }
      return this.step > 1 ? this.step - 1 : this.step
    },
    progressPct () {
      return Math.round((this.displayStep / this.displayTotalSteps) * 100)
    },
    stepTitle () {
      if (this.step === 3 && this.showReturningReview) {
        return 'Confirm details'
      }
      if (this.skipEmailStep) {
        return ['', 'Stay', 'You', 'You', 'Payments', 'Terms', 'Join'][this.step] || ''
      }
      return ['', 'Stay', 'Email', 'You', 'Payments', 'Terms', 'Join'][this.step] || ''
    },
    returningProfileComplete () {
      const digits = String(this.phone || '').replace(/\D/g, '')
      return !!(
        this.firstName.trim() &&
        this.lastName.trim() &&
        digits.length >= 10 &&
        mailingAddressComplete(this.mailingPayload())
      )
    },
    structuredMailing () {
      if (isStructuredMailingCountry(this.addressCountry)) {
        return true
      }
      // Don't hide street/city/ZIP if country was cleared mid-typing.
      if (!this.addressCountry) {
        return !!(this.addressCity || this.addressState || this.addressZip)
      }
      return false
    },
    countrySelectValue () {
      const code = normalizeCountryCode(this.addressCountry)
      if (!code) {
        return ''
      }
      if (MAILING_COUNTRIES.some((c) => c.code === code)) {
        return code
      }
      return 'OTHER'
    },
    mailingRegionLabel () {
      return regionLabel(this.addressCountry)
    },
    mailingPostalLabel () {
      return postalLabel(this.addressCountry)
    },
    showReturningReview () {
      return this.welcomeBack && !this.returningEditing && this.returningHadCompleteProfile
    },
    returningAddressLines () {
      return formatMailingLines(this.mailingPayload())
    },
    welcomeBackLine () {
      if (!this.welcomeBack) {
        return ''
      }
      const nth = ordinalYearLabel(this.returningYearNumber)
      const name = this.firstName ? ', ' + this.firstName : ''
      if (nth && this.returningYearNumber >= 2) {
        return 'Welcome back for your ' + nth + ' year' + name
      }
      return 'Welcome back' + name
    },
    formattedPhone () {
      return formatPhoneDisplay(this.phone)
    },
    carriedDepositLabel () {
      const year = this.depositCreditFromYear
      return this.depositLabel + ' Deposit Carried Over' +
        (year ? ' from ' + year : '')
    },
    registrationNow () {
      return new Date()
    },
    remainingFifths () {
      return remainingInstallmentFifths(this.registrationNow, this.registrationYear)
    },
    payRemainingTodayOption () {
      return isAfterFirstInstallment(this.registrationNow, this.registrationYear)
    },
    onlyPayInFullToday () {
      return this.remainingFifths.length === 0
    },
    selectedPlan () {
      return resolveInstallmentPlan(
        this.installmentCount,
        remainingCentsForRoom(this.room),
        this.registrationNow,
        this.registrationYear
      )
    },
    paymentPlanOptions () {
      const fifths = this.remainingFifths
      const remaining = remainingCentsForRoom(this.room)
      const deposit = this.depositLabel
      const credit = this.usingDepositCredit
      const afterJan = this.payRemainingTodayOption
      const remainingLabel = formatUsdFromCents(remaining)
      const payInFullSub = credit
        ? this.carriedDepositLabel + ' + the remaining ' + remainingLabel
        : '$' + depositUsd().toLocaleString('en-US') + ' Deposit + the remaining ' + remainingLabel
      if (!fifths.length) {
        return [{
          value: 0,
          icon: 1,
          title: 'Pay in full today',
          sub: payInFullSub
        }]
      }
      const plans = []
      if (afterJan) {
        plans.push({
          value: 0,
          icon: 1,
          title: 'Pay in full today',
          sub: payInFullSub
        })
        const n = fifths.length
        const firstName = fifths[0].label.replace(' 5', '')
        const lastName = fifths[fifths.length - 1].label.replace(' 5', '')
        const range = n === 1 ? fifths[0].label : (firstName + ' – ' + lastName)
        plans.push({
          value: n === 1 ? 2 : n,
          title: credit
            ? this.carriedDepositLabel + ' + ' + n + ' payment' + (n === 1 ? '' : 's') + ' ' + range
            : deposit + ' today + ' + n + ' payment' + (n === 1 ? '' : 's') + ' ' + range,
          sub: n === 1
            ? '$' + installmentEachUsd(remaining, 1).toLocaleString('en-US') + ' on ' + fifths[0].label
            : installmentEachMonthLabel(remaining, n)
        })
      } else {
        plans.push({
          value: 1,
          title: credit
            ? this.carriedDepositLabel + ' + remaining balance January 5'
            : deposit + ' today + remaining balance January 5',
          sub: 'Pay the rest in one go'
        })
        plans.push({
          value: 6,
          title: credit
            ? this.carriedDepositLabel + ' + 6 Payments January - June'
            : deposit + ' Today + 6 Payments January - June',
          sub: installmentEachMonthLabel(remaining, 6)
        })
      }
      return plans
    },
    dueTodayCents () {
      if (this.usingDepositCredit || this.cardSetupMode) {
        return 0
      }
      if (this.selectedPlan.payRemainingToday) {
        return DEPOSIT_CENTS + remainingCentsForRoom(this.room)
      }
      return DEPOSIT_CENTS
    },
    dueTodayLabel () {
      return formatUsdFromCents(this.dueTodayCents)
    },
    planSummaryLabel () {
      const match = this.paymentPlanOptions.find((p) => p.value === this.installmentCount)
      return (match && match.title) || 'Payment plan'
    },
    depositLabel () {
      return formatUsdFromCents(DEPOSIT_CENTS)
    },
    remainingLabel () {
      return formatUsdFromCents(remainingCentsForRoom(this.room))
    },
    packageTotalLabel () {
      return formatUsdFromCents(festivalTotalCentsForRoom(this.room))
    },
    roomLabel () {
      const opt = ROOM_OPTIONS.find(r => r.value === this.room)
      return opt ? opt.title : this.room
    },
    needsRoommateName () {
      return roomRequiresRoommateName(this.room)
    },
    roommateNameReady () {
      return String(this.roommateName || '').trim().length >= 2
    },
    totalTicketsRemaining () {
      const t = this.totalAvailability
      if (!t || t.limit == null) return null
      return t.remaining != null ? t.remaining : null
    },
    totalTicketsSoldOut () {
      const t = this.totalAvailability
      if (!t || t.limit == null) {
        return false
      }
      if (t.soldOut) {
        return true
      }
      const limit = Number(t.limit)
      const taken = Number(t.taken) || 0
      if (Number.isFinite(limit) && taken >= limit) {
        return true
      }
      if (t.remaining != null && Number(t.remaining) <= 0) {
        return true
      }
      return false
    },
    reservedRoomLabel () {
      if (!this.emailRoomReservation || !this.emailRoomReservation.room) return ''
      return lodgingChoiceTitle(this.emailRoomReservation.room)
    },
    holdExpiredRoomLabel () {
      if (!this.holdExpiredRoom) return ''
      return lodgingChoiceTitle(this.holdExpiredRoom)
    },
    roomChoices () {
      const byRoom = {}
      ;(this.roomAvailability || []).forEach(r => {
        byRoom[r.room] = r
      })
      const totalSoldOut = this.totalTicketsSoldOut
      const reservedRoom = this.emailRoomReservation && this.emailRoomReservation.room
      const choices = ROOM_OPTIONS.map(opt => {
        const av = byRoom[opt.value] || {}
        const isOnsiteRoom = ONSITE_ROOM_LIMIT_KEYS.indexOf(opt.value) !== -1
        const reservedForYou = !!(reservedRoom && opt.value === reservedRoom)
        const heldForYou = !!(
          this.holdId &&
          this.heldRoom === opt.value &&
          this.holdExpiresAt &&
          this.holdExpiresAt > Date.now()
        )
        // Snapshot soldOut is also true when the festival is full, even if this
        // room still has remaining beds. Offers need the physical remaining count.
        const roomPhysicallyGone = isOnsiteRoom && av.remaining != null
          ? Number(av.remaining) <= 0
          : (isOnsiteRoom && !!av.soldOut && !totalSoldOut)
        const soldOut = (heldForYou || reservedForYou)
          ? false
          : (this.waitlistOfferActive
            ? roomPhysicallyGone
            : (totalSoldOut || roomPhysicallyGone || !!av.soldOut))
        const totalCents = festivalTotalCentsForRoom(opt.value)
        return {
          ...opt,
          limit: av.limit != null ? av.limit : null,
          taken: Number(av.taken) || 0,
          remaining: av.remaining != null ? av.remaining : null,
          showRoomRemaining: isOnsiteRoom && av.limit != null && !reservedForYou,
          reservedForYou,
          heldForYou,
          soldOut,
          priceLabel: formatUsdFromCents(totalCents),
          pricePlan: 'or ' + formatUsdFromCents(DEPOSIT_CENTS) + ' today + 6 monthly payments'
        }
      })
      return choices.sort((a, b) => {
        const as = (a.reservedForYou || a.heldForYou) ? 0 : 1
        const bs = (b.reservedForYou || b.heldForYou) ? 0 : 1
        return as - bs
      })
    },
    holdActive () {
      return !!(this.holdId && this.holdExpiresAt && this.holdSecondsLeft > 0)
    },
    holdReadyForSelection () {
      return !!(
        this.holdId &&
        this.heldRoom === this.room &&
        this.holdExpiresAt &&
        this.holdExpiresAt > Date.now()
      )
    },
    holdCountdownLabel () {
      const s = Math.max(0, this.holdSecondsLeft)
      const m = Math.floor(s / 60)
      const sec = s % 60
      return m + ':' + String(sec).padStart(2, '0')
    },
    addressSummary () {
      return formatMailingLines(this.mailingPayload()).join(' · ')
    },
    usingDepositCredit () {
      return !!(this.hasHeldDeposit && this.depositCreditCents >= this.depositCents)
    },
    roomIsSoldOut () {
      const c = this.roomChoices.find(o => o.value === this.room)
      return !!(c && c.soldOut)
    },
    justTakenRoomLabel () {
      return lodgingChoiceTitle(this.justTakenRoom)
    }
  },
  watch: {
    error (msg) {
      if (!msg) {
        return
      }
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('.error-banner--cta')
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    loggedInEmail (email) {
      if (!email || this.step !== 1 || this.alreadyRegistered2027) {
        return
      }
      if (this.emailRoomReservation && this.email === email) {
        return
      }
      this.prefetchLoggedInReservation()
    },
    step (n) {
      if (n === 1) {
        this.startRoomAvailabilityListener()
      }
      if (n === 6 && this.usingDepositCredit) {
        this.cardSetupMode = true
        this.$nextTick(() => {
          this.startEmbeddedCardSetup(this.enrollmentId || null)
        })
      }
      if (n === 6 && !this.usingDepositCredit) {
        this.startEmbeddedPayment()
      }
      if (n !== 6) {
        this.teardownStripe()
        this.payPreparing = false
        this.cardSetupMode = false
      }
      // Keep listener active for whole funnel so sold-out state stays live if they go back
    },
    paymentPlanOptions: {
      immediate: true,
      handler (plans) {
        if (!plans || !plans.length) {
          return
        }
        if (!plans.some((p) => p.value === this.installmentCount)) {
          this.installmentCount = plans[plans.length - 1].value
        }
      }
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.restoreDraft()
    this.syncLoggedInEmail()
    this.loadWaitlistOfferFromQuery()
    this.loadAccessGate().then(() => {
      if (this.skipEmailStep && this.step === 2 && !this.alreadyRegistered2027) {
        this.continueFromEmail()
      } else if (this.skipEmailStep && this.step === 1) {
        this.prefetchLoggedInReservation()
      }
    })
    // Seed snapshot once (also reconciles counters), then listen in realtime
    this.bootstrapRoomAvailability()
    if (this.holdExpiresAt && this.holdId) {
      if (this.holdExpiresAt <= Date.now()) {
        this.expireHoldOnClient()
      } else {
        this.startHoldTimer()
      }
    }
    if (this.step === 6 && this.usingDepositCredit) {
      this.cardSetupMode = true
      this.$nextTick(() => { this.startEmbeddedCardSetup(this.enrollmentId || null) })
    }
    if (this.step === 6 && !this.usingDepositCredit) {
      this.$nextTick(() => { this.startEmbeddedPayment() })
    }
    this._onHoldVis = () => { this.kickBackIfHoldExpired() }
    document.addEventListener('visibilitychange', this._onHoldVis)
    window.addEventListener('focus', this._onHoldVis)
  },
  beforeDestroy () {
    this.clearHoldTimer()
    this.teardownStripe()
    this.dismissHoldExpired()
    this.stopRoomAvailabilityListener()
    if (this._onHoldVis) {
      document.removeEventListener('visibilitychange', this._onHoldVis)
      window.removeEventListener('focus', this._onHoldVis)
    }
  },
  methods: {
    selectRoom (opt) {
      if (!opt || this.busy) {
        return
      }
      if (opt.soldOut) {
        return
      }
      this.cottageFallbackOffer = false
      this.room = opt.value
      if (!roomRequiresRoommateName(opt.value)) {
        this.roommateName = ''
      }
      this.justTakenBanner = false
      this.dismissHoldExpired()
      this.error = ''
      // Friend needs a name first. Everything else: the tap is the save.
      if (roomRequiresRoommateName(opt.value)) {
        return
      }
      this.continueFromRoom()
    },
    /**
     * Live capacity from Firestore doc updated by Cloud Functions on every hold/release.
     * Instant under flurry — no polling.
     */
    startRoomAvailabilityListener () {
      if (this.roomAvailabilityUnsub) {
        return
      }
      const configCol = iabfForYear(REGISTRATION_YEAR).config
      if (!configCol) {
        return
      }
      this.roomsLoading = !this.roomAvailability.length
      this.roomAvailabilityUnsub = db.collection(configCol).doc('room-availability')
        .onSnapshot(
          (snap) => {
            this.roomsLoading = false
            if (!snap.exists) {
              return
            }
            const data = snap.data() || {}
            const roomsMap = data.rooms || {}
            this.roomAvailability = Object.keys(roomsMap).map(room => ({
              room,
              limit: roomsMap[room].limit != null ? roomsMap[room].limit : null,
              taken: Number(roomsMap[room].taken) || 0,
              remaining: roomsMap[room].remaining != null ? roomsMap[room].remaining : null,
              soldOut: !!roomsMap[room].soldOut
            }))
            const total = data.total || null
            this.totalAvailability = total
              ? {
                limit: total.limit != null ? total.limit : null,
                taken: Number(total.taken) || 0,
                remaining: total.remaining != null ? total.remaining : null,
                soldOut: !!total.soldOut
              }
              : null
            // If selected room just sold out under us, clear selection —
            // unless this tab already holds it.
            if (this.room && this.step === 1) {
              const choice = this.roomChoices.find(r => r.value === this.room)
              if (choice && choice.soldOut) {
                this.room = ''
                this.roommateName = ''
              }
            }
          },
          (err) => {
            console.warn('room-availability listener error', err)
            this.roomsLoading = false
          }
        )
    },
    stopRoomAvailabilityListener () {
      if (typeof this.roomAvailabilityUnsub === 'function') {
        this.roomAvailabilityUnsub()
      }
      this.roomAvailabilityUnsub = null
    },
    bootstrapRoomAvailability () {
      // Fast path: listen to the precomputed Firestore summary immediately.
      // Do NOT await a Cloud Function recount on load — that full-scans enrollments
      // and is why this step used to take 1–2+ seconds.
      this.roomsLoading = true
      this.startRoomAvailabilityListener()
      // Background: if the summary doc is missing (first run / empty), seed it once.
      // Non-blocking — UI already paints from onSnapshot when the doc exists.
      apiRoomAvailability().catch(() => { /* ignore */ })
      // Deposit still lives on Cloud Functions — wake that isolate for the pay step.
      fetch(cfBase() + '/createRegistrationDepositCheckoutSession', { method: 'OPTIONS' }).catch(() => { /* ignore */ })
    },
    async loadRoomAvailability () {
      // Expire stale holds + re-read counters. Does not scan dancers.
      try {
        await apiRoomAvailability({ reconcile: true })
      } catch (e) {
        console.warn('room availability refresh failed', e)
      }
    },
    async loadAccessGate () {
      this.accessLoading = true
      try {
        const access = await this.$store.dispatch('loadRegistrationAccessGate')
        this.accessMode = (access && access.mode) || 'closed'
        this.publicOpensAt = (access && access.publicOpensAt) || null
      } catch (e) {
        // Fail closed — server also enforces; never flash the form if config is missing
        this.accessMode = 'closed'
      }
      this.accessLoading = false
    },
    startHoldTimer () {
      this.clearHoldTimer()
      const tick = () => {
        if (!this.holdExpiresAt) {
          this.holdSecondsLeft = 0
          return
        }
        const left = Math.max(0, Math.ceil((this.holdExpiresAt - Date.now()) / 1000))
        this.holdSecondsLeft = left
        if (left <= 0) {
          this.expireHoldOnClient()
        }
      }
      tick()
      this.holdTimer = setInterval(tick, 1000)
    },
    clearHoldTimer () {
      if (this.holdTimer) {
        clearInterval(this.holdTimer)
        this.holdTimer = null
      }
    },
    kickBackIfHoldExpired () {
      if (!this.holdId || !this.holdExpiresAt) {
        return false
      }
      if (this.holdExpiresAt > Date.now()) {
        return false
      }
      this.expireHoldOnClient()
      return true
    },
    expireHoldOnClient () {
      if (this.holdExpiring) {
        return
      }
      this.holdExpiring = true
      this.clearHoldTimer()
      const id = this.holdId
      const expiredRoom = this.heldRoom || this.room
      this.holdId = null
      this.heldRoom = ''
      this.holdExpiresAt = null
      this.holdSecondsLeft = 0
      this.room = ''
      this.roommateName = ''
      this.busy = false
      this.error = ''
      this.justTakenBanner = false
      this.holdExpiredRoom = expiredRoom
      this.holdExpiredBanner = true
      this.holdReleasing = !!id
      this.teardownStripe()
      this.cardSetupMode = false
      this.step = 1
      this.persistDraft()
      this.scheduleHoldExpiredDismiss()
      this.finishHoldRelease(id)
    },
    scheduleHoldExpiredDismiss () {
      if (this.holdExpiredTimer) {
        clearTimeout(this.holdExpiredTimer)
      }
      this.holdExpiredTimer = setTimeout(() => {
        this.holdExpiredBanner = false
        this.holdExpiredTimer = null
      }, 5000)
    },
    dismissHoldExpired () {
      if (this.holdExpiredTimer) {
        clearTimeout(this.holdExpiredTimer)
        this.holdExpiredTimer = null
      }
      this.holdExpiredBanner = false
    },
    async finishHoldRelease (id) {
      if (id) {
        try {
          await apiReleaseHold(id)
        } catch (e) { /* sweeper will catch it */ }
      }
      this.holdReleasing = false
      this.holdExpiring = false
    },
    onHoldExpired () {
      this.expireHoldOnClient()
    },
    clearAlreadyRegistered () {
      this.alreadyRegistered2027 = false
      this.loginPassword = ''
      this.loginPasswordConfirm = ''
      this.loginError = ''
      this.resetSent = false
      this.error = ''
    },
    async loginAlreadyRegistered () {
      const email = this.email.trim().toLowerCase()
      const password = this.loginPassword
      if (!email || !password) {
        this.loginError = 'Please enter your password.'
        return
      }
      this.loginError = ''
      this.busy = true
      try {
        if (this.hasAuth) {
          await this.$store.dispatch('signInIABFDancer', { email, password })
        } else {
          if (password !== this.loginPasswordConfirm) {
            this.loginError = 'Those passwords don’t match.'
            this.busy = false
            return
          }
          if (password.length < 6) {
            this.loginError = 'Please use at least 6 characters.'
            this.busy = false
            return
          }
          await this.$store.dispatch('signUpIABFDancer', { email, password })
        }
        this.$router.push('/')
      } catch (e) {
        const msg = (e && e.message) || ''
        this.loginError = /password/i.test(msg)
          ? 'That password didn’t work. Try again or reset it.'
          : (msg.replace(/^[^:]+:\s*/, '') || 'Could not log in. Please try again.')
      }
      this.busy = false
    },
    async resetAlreadyRegisteredPassword () {
      const email = this.email.trim().toLowerCase()
      if (!email) {
        this.loginError = 'Please enter your email first.'
        return
      }
      this.loginError = ''
      this.resetSent = false
      try {
        const sendReset = functions.httpsCallable('iabfSendPasswordReset')
        await sendReset({ email })
        this.resetSent = true
      } catch (e) {
        this.loginError = 'Could not send a reset email. Please try again or write hello@iabf.dance.'
      }
    },
    async goBackToRoom () {
      this.clearAlreadyRegistered()
      this.cottageFallbackOffer = false
      this.step = 1
      this.error = ''
      this.persistDraft()
    },
    async releaseCurrentHold () {
      const id = this.holdId
      this.clearHoldTimer()
      this.holdId = null
      this.heldRoom = ''
      this.holdExpiresAt = null
      this.holdSecondsLeft = 0
      if (!id) return
      try {
        await apiReleaseHold(id)
      } catch (e) { /* ignore */ }
    },
    formatUsd (cents) {
      return formatUsdFromCents(cents)
    },
    monthlyPaymentUsd (count) {
      const n = Number(count) > 0 ? Number(count) : 1
      return installmentEachUsd(remainingCentsForRoom(this.room), n)
    },
    planOptionTitle (opt) {
      return opt.title
    },
    planOptionSub (opt) {
      return opt.sub
    },
    installmentPreview (count) {
      const remaining = remainingCentsForRoom(this.room)
      if (Number(count) === 1) {
        return '$' + installmentEachUsd(remaining, 1).toLocaleString('en-US') + ' on January 5'
      }
      return installmentEachMonthLabel(remaining, count)
    },
    mailingPayload () {
      return {
        addressStreet: this.addressStreet,
        addressStreet2: this.addressStreet2,
        addressCity: this.addressCity,
        addressState: this.addressState,
        addressZip: this.addressZip,
        addressCountry: this.addressCountry
      }
    },
    onCountryChange (code) {
      const next = String(code || '').trim()
      if (!next || next === '_divider') {
        return
      }
      const wasStructured = isStructuredMailingCountry(this.addressCountry)
      const nowStructured = isStructuredMailingCountry(next)
      if (wasStructured && !nowStructured) {
        const folded = foldIntoEnvelope(this.mailingPayload())
        this.addressStreet = folded.addressStreet
        this.addressStreet2 = folded.addressStreet2
        this.addressCity = folded.addressCity
        this.addressState = folded.addressState
        this.addressZip = folded.addressZip
      }
      this.addressCountry = next
    },
    stashSuccessAuth () {
      try {
        sessionStorage.setItem(SUCCESS_AUTH_KEY, JSON.stringify({
          email: this.email.trim().toLowerCase(),
          hasAuth: !!this.hasAuth
        }))
      } catch (e) { /* ignore */ }
    },
    successQuery (extra) {
      const query = Object.assign({}, extra || {})
      const email = this.email.trim().toLowerCase()
      if (email) {
        query.email = email
      }
      if (this.hasAuth) {
        query.hasAuth = '1'
      }
      return query
    },
    persistDraft () {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          step: this.step,
          email: this.email,
          alreadyRegistered2027: this.alreadyRegistered2027,
          welcomeBack: this.welcomeBack,
          returningYearNumber: this.returningYearNumber,
          returningEditing: this.returningEditing,
          returningHadCompleteProfile: this.returningHadCompleteProfile,
          hasAuth: this.hasAuth,
          firstName: this.firstName,
          lastName: this.lastName,
          phone: this.phone,
          addressStreet: this.addressStreet,
          addressStreet2: this.addressStreet2,
          addressCity: this.addressCity,
          addressState: this.addressState,
          addressZip: this.addressZip,
          addressCountry: this.addressCountry,
          room: this.room,
          roommateName: this.roommateName,
          holdId: this.holdId,
          holdExpiresAt: this.holdExpiresAt,
          heldRoom: this.heldRoom,
          enrollmentId: this.enrollmentId,
          installmentCount: this.installmentCount,
          termsAccepted: this.termsAccepted,
          hasHeldDeposit: this.hasHeldDeposit,
          depositCreditCents: this.depositCreditCents,
          depositCreditFromYear: this.depositCreditFromYear,
          emailRoomReservation: this.emailRoomReservation
        }))
      } catch (e) { /* ignore */ }
    },
    restoreDraft () {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const d = JSON.parse(raw)
        Object.keys(d).forEach(k => {
          if (this[k] !== undefined && d[k] !== undefined) {
            this[k] = d[k]
          }
        })
        // Drop expired holds from draft
        if (this.holdExpiresAt && this.holdExpiresAt <= Date.now()) {
          this.holdId = null
          this.heldRoom = ''
          this.holdExpiresAt = null
          this.room = ''
          this.roommateName = ''
          this.step = 1
        }
      } catch (e) { /* ignore */ }
    },
    clearDraft () {
      try {
        sessionStorage.removeItem(STORAGE_KEY)
      } catch (e) { /* ignore */ }
    },
    payload () {
      return {
        email: this.email.trim().toLowerCase(),
        firstName: this.firstName.trim(),
        lastName: this.lastName.trim(),
        phone: this.phone.trim(),
        addressStreet: this.addressStreet.trim(),
        addressStreet2: this.addressStreet2.trim(),
        addressCity: this.addressCity.trim(),
        addressState: this.addressState.trim(),
        addressZip: this.addressZip.trim(),
        addressCountry: normalizeCountryCode(this.addressCountry),
        room: this.room,
        roommateName: roomRequiresRoommateName(this.room)
          ? String(this.roommateName || '').trim()
          : '',
        holdId: this.holdId,
        installmentCount: this.installmentCount,
        termsAccepted: this.termsAccepted
      }
    },
    retryEarlyEmail () {
      this.earlyWaitlisted = false
      this.earlyAccessEligible = false
      this.error = ''
      if (this.skipEmailStep) {
        auth.signOut().catch(() => { /* ignore */ })
        this.$store.commit('setUser', null)
        this.$store.commit('setCurrentIABFDancer', null)
        this.email = ''
        this.hasAuth = false
      }
      this.step = 2
    },
    syncLoggedInEmail () {
      const email = this.loggedInEmail
      if (!email) {
        return
      }
      this.email = email
      this.hasAuth = true
      if (!this.waitlistEmail) {
        this.waitlistEmail = email
      }
    },
    async joinWaitlist () {
      this.waitlistError = ''
      const email = String(this.waitlistEmail || this.email || '').trim().toLowerCase()
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.waitlistError = 'Please enter a valid email address.'
        return
      }
      this.waitlistBusy = true
      try {
        const result = await this.$store.dispatch('joinIabfRegistrationWaitlist', {
          email,
          firstName: this.firstName
        })
        this.waitlistJoined = true
        this.waitlistAlready = !!(result && result.already)
        this.email = email
      } catch (e) {
        this.waitlistError = (e && e.message) || 'Could not save your place. Please try again.'
      }
      this.waitlistBusy = false
    },
    async loadWaitlistOfferFromQuery () {
      const token = String((this.$route.query && this.$route.query.offer) || '').trim()
      if (!token) {
        return
      }
      try {
        const offer = await this.$store.dispatch('lookupIabfWaitlistOffer', token)
        this.waitlistOffer = offer
        if (offer && !offer.expired && offer.email) {
          this.email = offer.email
          this.waitlistEmail = offer.email
          if (offer.firstName && !this.firstName) {
            this.firstName = offer.firstName
          }
        }
      } catch (e) {
        console.warn('waitlist offer lookup failed', e)
        this.waitlistOffer = null
      }
    },
    async markWaitlistIfOffered (enrollmentId) {
      if (!this.waitlistOfferActive || !this.waitlistOffer) {
        return
      }
      try {
        await this.$store.dispatch('markIabfWaitlistRegistered', {
          waitlistId: this.waitlistOffer.waitlistId,
          offerToken: this.waitlistOffer.token,
          enrollmentId: enrollmentId || this.enrollmentId || null
        })
      } catch (e) {
        console.warn('waitlist register mark failed', e)
      }
    },
    backFromAbout () {
      this.step = this.skipEmailStep ? 1 : 2
    },
    async prefetchLoggedInReservation () {
      if (!this.loggedInEmail) {
        return
      }
      this.syncLoggedInEmail()
      try {
        const lookupRes = await apiLookupEmail(this.email)
        const data = lookupRes.data || {}
        if (!lookupRes.ok || !data.roomReservation || !data.roomReservation.room) {
          return
        }
        this.emailRoomReservation = {
          room: data.roomReservation.room,
          notes: data.roomReservation.notes || ''
        }
        this.persistDraft()
      } catch (e) {
        console.warn('saved-room prefetch failed', e)
      }
    },
    async unlockSavedRoom () {
      const email = String(this.savedRoomEmail || '').trim().toLowerCase()
      this.savedRoomMiss = ''
      this.email = email
      await this.continueFromEmail({ stayOnRoomStep: true })
      if (this.emailRoomReservation && this.emailRoomReservation.room) {
        this.savedRoomMiss = ''
        return
      }
      if (!this.alreadyRegistered2027 && !this.earlyWaitlisted && !this.error) {
        this.savedRoomMiss = 'We don’t have a saved onsite room for that email. You’re welcome to pick any open stay.'
      }
    },
    async continueFromEmail (opts) {
      const stayOnRoomStep = !!(opts && opts.stayOnRoomStep)
      if (!stayOnRoomStep && this.kickBackIfHoldExpired()) {
        return
      }
      this.error = ''
      const email = this.email.trim().toLowerCase()
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.error = 'Please enter a valid email address.'
        return
      }
      this.busy = true
      try {
        const [methods, lookupRes] = await Promise.all([
          auth.fetchSignInMethodsForEmail(email).catch(() => []),
          apiLookupEmail(email)
        ])
        const data = lookupRes.data || {}
        const hasAuth = !!(data.hasAuth || (methods && methods.length))
        this.hasAuth = hasAuth
        if (!lookupRes.ok) {
          this.error = lookupRes.message || data.error || 'Could not check that email. Please try again.'
          this.busy = false
          return
        }

        if (data.alreadyRegistered2027) {
          this.alreadyRegistered2027 = true
          this.error = ''
          this.loginPassword = ''
          this.loginPasswordConfirm = ''
          this.loginError = ''
          this.resetSent = false
          this.busy = false
          await this.releaseCurrentHold()
          const currentEmail = auth.currentUser && String(auth.currentUser.email || '').trim().toLowerCase()
          if (currentEmail && currentEmail === email) {
            this.$router.push('/')
            return
          }
          this.step = 2
          return
        }

        // Early-access gate: returners and canceled dancers when public is off
        this.earlyAccessEligible = !!(data.earlyAccessEligible || data.welcomeBack || data.hasHeldDeposit)
        if (this.accessMode === 'early' && !this.earlyAccessEligible) {
          await this.releaseCurrentHold()
          this.earlyWaitlisted = true
          this.busy = false
          return
        }
        if (this.accessMode === 'closed') {
          this.error = 'Registration isn’t open yet. Please check back soon or email hello@iabf.dance.'
          this.busy = false
          return
        }

        this.welcomeBack = !!(data.welcomeBack || hasAuth)
        this.returningYearNumber = Number(data.returningYearNumber) || 0
        this.returningEditing = false
        // Always replace — a prior lookup (or draft) must not stick to a new email.
        this.firstName = String(data.firstName || '').trim()
        this.lastName = String(data.lastName || '').trim()
        this.phone = String(data.phone || '').trim()
        this.addressStreet = String(data.addressStreet || '').trim()
        this.addressStreet2 = String(data.addressStreet2 || '').trim()
        this.addressCity = String(data.addressCity || '').trim()
        this.addressState = String(data.addressState || '').trim()
        this.addressZip = String(data.addressZip || '').trim()
        this.addressCountry = normalizeCountryCode(data.addressCountry)
        if (!this.addressCountry && mailingAddressComplete({
          addressStreet: this.addressStreet,
          addressCity: this.addressCity,
          addressState: this.addressState,
          addressZip: this.addressZip,
          addressCountry: 'US'
        })) {
          this.addressCountry = 'US'
        }
        // Snapshot completeness at lookup only. Filling ZIP must not flip
        // the form into the "still you?" review card.
        this.returningHadCompleteProfile = this.returningProfileComplete
        this.hasHeldDeposit = !!data.hasHeldDeposit
        this.depositCreditCents = Number(data.depositCreditCents) || 0
        this.depositCreditFromYear = data.depositCreditFromYear || null
        this.email = email

        // Admin set-aside: lock room + re-hold so inventory uses the email reservation path
        if (data.roomReservation && data.roomReservation.room) {
          this.emailRoomReservation = {
            room: data.roomReservation.room,
            notes: data.roomReservation.notes || ''
          }
          const reservedRoom = data.roomReservation.room
          if (this.room !== reservedRoom) {
            this.room = reservedRoom
            this.roommateName = ''
          }
          // Re-create hold with email so set-aside skips public room inventory
          try {
            const holdRes = await apiCreateHold({
              room: reservedRoom,
              priorHoldId: this.holdId || null,
              email,
              uid: (auth.currentUser && auth.currentUser.uid) || null
            })
            const holdData = holdRes.data || {}
            if (holdRes.ok) {
              this.holdId = holdData.holdId
              this.holdExpiresAt = Number(holdData.expiresAt) || (Date.now() + REGISTRATION_HOLD_MS)
              this.startHoldTimer()
            } else if (holdRes.message || holdData.message || holdData.error) {
              this.error = holdRes.message || holdData.message || holdData.error
            }
          } catch (holdErr) {
            console.warn('re-hold with email reservation failed', holdErr)
          }
        } else {
          this.emailRoomReservation = null
        }

        if (!stayOnRoomStep) {
          this.step = 3
        }
        this.persistDraft()
      } catch (e) {
        console.error(e)
        this.error = (e && e.message) || 'Could not check that email. Please try again.'
      }
      this.busy = false
    },
    confirmReturningDetails () {
      if (this.kickBackIfHoldExpired()) {
        return
      }
      if (!mailingAddressComplete(this.mailingPayload())) {
        this.returningEditing = true
        this.error = 'We don’t have a complete mailing address yet. Please add it below.'
        return
      }
      this.continueFromAbout()
    },
    continueFromAbout () {
      if (this.kickBackIfHoldExpired()) {
        return
      }
      this.error = ''
      if (!this.firstName.trim() || !this.lastName.trim()) {
        this.error = 'Please enter your first and last name.'
        return
      }
      const digits = String(this.phone).replace(/\D/g, '')
      if (digits.length < 10) {
        this.error = 'Please enter a valid phone number (at least 10 digits).'
        return
      }
      this.addressCountry = normalizeCountryCode(this.addressCountry)
      const mailingErr = mailingAddressError(this.mailingPayload())
      if (mailingErr) {
        this.error = mailingErr
        return
      }
      this.step = 4
      this.persistDraft()
    },
    queueHold (room) {
      this.pendingHoldRoom = room
      return this.drainHoldQueue()
    },
    async drainHoldQueue () {
      if (this.holdQueuePromise) {
        await this.holdQueuePromise
        if (this.pendingHoldRoom) {
          return this.drainHoldQueue()
        }
        return
      }
      this.holdInFlight = true
      this.holdQueuePromise = (async () => {
        try {
          while (this.pendingHoldRoom) {
            const room = this.pendingHoldRoom
            this.pendingHoldRoom = null
            if (this.holdId && this.heldRoom === room && this.holdExpiresAt > Date.now()) {
              continue
            }
            const held = await this.createHoldForRoom(room)
            if (held === 'fallback') {
              break
            }
          }
        } finally {
          this.holdInFlight = false
          this.holdQueuePromise = null
        }
      })()
      await this.holdQueuePromise
      if (this.pendingHoldRoom) {
        return this.drainHoldQueue()
      }
    },
    async createHoldForRoom (room) {
      try {
        const res = await apiCreateHold({
          room,
          roommateName: roomRequiresRoommateName(room)
            ? String(this.roommateName || '').trim()
            : null,
          priorHoldId: this.holdId || null,
          email: (this.email || (auth.currentUser && auth.currentUser.email) || '').trim().toLowerCase() || null,
          uid: (auth.currentUser && auth.currentUser.uid) || null,
          offerToken: (this.waitlistOfferActive && this.waitlistOffer && this.waitlistOffer.token) || null
        })
        const data = res.data || {}
        if (!res.ok) {
          if (this.room !== room) {
            return false
          }
          if (data.error === 'sold_out' || data.code === 'just_taken' ||
              data.code === 'room_sold_out' || data.code === 'tickets_sold_out' ||
              res.status === 409) {
            this.showJustTaken(room || data.room)
          } else {
            this.error = res.message || data.message || data.error || 'Could not hold that spot. Please try again.'
          }
          return false
        }
        this.holdId = data.holdId
        this.heldRoom = data.room || room
        this.holdExpiresAt = Number(data.expiresAt) || (Date.now() + REGISTRATION_HOLD_MS)
        if (data.room) {
          this.room = data.room
        }
        if (data.emailReservation && data.emailReservation.room) {
          this.emailRoomReservation = {
            room: data.emailReservation.room
          }
        }
        this.startHoldTimer()
        this.persistDraft()
        if (data.fallbackFrom === 'Cottage') {
          this.cottageFallbackOffer = true
          return 'fallback'
        }
        this.cottageFallbackOffer = false
        return true
      } catch (e) {
        console.error(e)
        if (this.room === room) {
          this.error = 'Could not hold that spot. Please try again.'
        }
        return false
      }
    },
    async continueFromRoom (opts) {
      const advance = !opts || opts.advance !== false
      this.error = ''
      this.justTakenBanner = false
      if (!this.room) {
        this.error = 'Please choose a ticket type.'
        return
      }
      if (this.needsRoommateName && !this.roommateNameReady) {
        this.error = 'Please enter the name of the person who has the onsite room.'
        return
      }
      const choice = this.roomChoices.find(c => c.value === this.room)
      if (choice && choice.soldOut && !choice.reservedForYou && !choice.heldForYou) {
        this.showJustTaken(this.room)
        return
      }
      if (advance) {
        this.busy = true
      }
      try {
        if (!this.holdReadyForSelection) {
          this.pendingHoldRoom = this.room
          await this.drainHoldQueue()
        }
        if (!this.holdReadyForSelection) {
          this.busy = false
          return
        }
        if (this.cottageFallbackOffer) {
          this.busy = false
          this.persistDraft()
          return
        }
        this.justTakenBanner = false
        if (advance) {
          if (this.skipEmailStep) {
            this.syncLoggedInEmail()
            await this.continueFromEmail()
            this.persistDraft()
            return
          }
          this.step = 2
        }
        this.persistDraft()
      } catch (e) {
        console.error(e)
        this.error = 'Could not hold that spot. Please try again.'
      }
      this.busy = false
    },
    showJustTaken (roomValue) {
      this.justTakenRoom = roomValue || this.room || ''
      this.justTakenBanner = true
      this.cottageFallbackOffer = false
      if (this.holdId && this.heldRoom) {
        this.room = this.heldRoom
      } else {
        this.room = ''
        this.roommateName = ''
      }
      this.step = 1
      this.error = ''
      this.persistDraft()
      this.loadRoomAvailability()
    },
    acceptCottageFallback () {
      this.cottageFallbackOffer = false
      this.room = 'Standard Dorm'
      this.continueFromRoom()
    },
    dismissCottageFallback () {
      this.cottageFallbackOffer = false
    },
    continueFromPlan () {
      if (this.kickBackIfHoldExpired()) {
        return
      }
      this.error = ''
      const allowed = this.paymentPlanOptions.some((p) => p.value === this.installmentCount)
      if (!allowed) {
        this.error = 'Please choose a payment plan.'
        return
      }
      this.step = 5
      this.persistDraft()
    },
    continueFromTerms () {
      if (this.kickBackIfHoldExpired()) {
        return
      }
      this.error = ''
      if (!this.termsAccepted) {
        this.error = 'Please accept the Terms of Service and Cancellation Policy to continue.'
        return
      }
      this.step = 6
      this.persistDraft()
    },
    ensureHoldAlive () {
      if (this.kickBackIfHoldExpired()) {
        return false
      }
      if (!this.holdId || !this.holdExpiresAt) {
        this.expireHoldOnClient()
        return false
      }
      return true
    },
    stripeAppearance () {
      return {
        theme: 'stripe',
        variables: {
          borderRadius: '12px',
          fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
          colorPrimary: '#4a5d3a',
          colorBackground: '#ffffff',
          colorText: '#1c1b1f'
        }
      }
    },
    rememberEnrollment (id) {
      this.enrollmentId = id || this.enrollmentId
      if (!this.enrollmentId) {
        return
      }
      try {
        sessionStorage.setItem('iabf-reg-2027-enrollment', this.enrollmentId)
      } catch (e) { /* ignore */ }
      this.persistDraft()
    },
    teardownStripe () {
      try {
        if (this._paymentElement && typeof this._paymentElement.unmount === 'function') {
          this._paymentElement.unmount()
        }
      } catch (e) { /* ignore */ }
      this._paymentElement = null
      this._stripeElements = null
      this._stripe = null
      this._stripeMode = null
      this.stripeReady = false
    },
    applyHoldRefresh (expiresAt) {
      const next = Number(expiresAt) || 0
      if (next > Date.now()) {
        this.holdExpiresAt = next
        this.startHoldTimer()
      }
    },
    handlePayStartError (data) {
      if (data.error === 'already_registered') {
        this.error = data.message || 'This email is already registered.'
        return
      }
      if (data.error === 'hold_expired' || data.error === 'sold_out') {
        this.error = data.message || 'Your hold expired or the room is full.'
        this.onHoldExpired()
        return
      }
      this.error = data.error || data.message || 'Could not start payment. Please try again.'
    },
    async startEmbeddedPayment () {
      if (this.usingDepositCredit || this.cardSetupMode) {
        return
      }
      if (this.payPreparing) {
        return
      }
      if (this._stripe && this._paymentElement && this._stripeMode === 'payment') {
        return
      }
      this.error = ''
      if (!this.ensureHoldAlive()) {
        return
      }
      this.payPreparing = true
      this.stripeReady = false
      try {
        const res = await fetch(
          cfBase() + '/createRegistrationDepositCheckoutSession',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...this.payload(),
              successUrl: window.location.origin + '/register/success?' +
                new URLSearchParams(this.successQuery()).toString(),
              cancelUrl: window.location.origin + '/register'
            })
          }
        )
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          this.handlePayStartError(data)
          this.payPreparing = false
          return
        }
        if (data.enrollmentId) {
          this.rememberEnrollment(data.enrollmentId)
        }
        this.applyHoldRefresh(data.holdExpiresAt)
        if (data.clientSecret) {
          await this.mountStripeElement({
            clientSecret: data.clientSecret,
            publishableKey: data.publishableKey,
            mode: 'payment'
          })
          return
        }
        if (data.url) {
          this.clearHoldTimer()
          this.stashSuccessAuth()
          this.clearDraft()
          window.location.href = data.url
          return
        }
        this.error = 'Could not open the card form. Please try again.'
      } catch (e) {
        console.error(e)
        this.error = 'Could not start payment. Please try again.'
      }
      this.payPreparing = false
    },
    async mountStripeElement ({ clientSecret, publishableKey, mode }) {
      const pk = String(publishableKey || process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || '').trim()
      if (!pk) {
        this.error = 'We couldn’t open the card form just now. Write us at hello@iabf.dance and we’ll help you finish.'
        this.payPreparing = false
        return
      }
      const StripeCtor = await loadStripeJs()
      if (!StripeCtor) {
        this.error = 'Could not load the card form. Please refresh and try again.'
        this.payPreparing = false
        return
      }
      this.teardownStripe()
      this._stripe = StripeCtor(pk)
      this._stripeMode = mode
      this._stripeElements = this._stripe.elements({
        clientSecret,
        appearance: this.stripeAppearance()
      })
      this._paymentElement = this._stripeElements.create('payment', {
        layout: 'tabs',
        // Link’s extra email/phone (“save for faster checkout”) looks required and
        // can leave our Pay button disabled if `ready` already fired. We already
        // have their email from registration.
        wallets: {
          link: 'never'
        }
      })
      await this.$nextTick()
      const mountEl = document.getElementById('reg-card-element')
      if (!mountEl) {
        this.error = 'Could not open the card form. Please go back and try again.'
        this.payPreparing = false
        return
      }
      const markReady = () => {
        this.payPreparing = false
        this.stripeReady = true
      }
      // Listen before mount — `ready` can fire during mount() and be missed.
      this._paymentElement.on('ready', markReady)
      this._paymentElement.on('change', (event) => {
        if (event && event.complete) {
          markReady()
        }
      })
      this._paymentElement.on('loaderror', () => {
        this.payPreparing = false
        this.stripeReady = false
        this.error = 'Could not open the card form. Please refresh and try again.'
      })
      this._paymentElement.mount(mountEl)
    },
    async payDeposit () {
      this.error = ''
      if (!this.ensureHoldAlive()) {
        return
      }
      if (!this._stripe || !this._stripeElements || this._stripeMode !== 'payment') {
        await this.startEmbeddedPayment()
        return
      }
      this.busy = true
      try {
        const returnUrl = window.location.origin + '/register/success?' +
          new URLSearchParams(this.successQuery(
            this.enrollmentId ? { enrollmentId: this.enrollmentId } : {}
          )).toString()
        const result = await this._stripe.confirmPayment({
          elements: this._stripeElements,
          confirmParams: {
            return_url: returnUrl,
            receipt_email: this.email.trim().toLowerCase(),
            payment_method_data: {
              billing_details: {
                name: (this.firstName + ' ' + this.lastName).trim(),
                email: this.email.trim().toLowerCase(),
                phone: this.phone
              }
            }
          },
          redirect: 'if_required'
        })
        if (result.error) {
          this.error = result.error.message || 'That card did not go through. Please try again.'
          this.busy = false
          return
        }
        const pi = result.paymentIntent
        if (pi && pi.status === 'succeeded') {
          await this.finishPaidRegistration(pi.id)
          return
        }
        this.error = 'Your bank is still confirming. Give it a moment, then try again — or write hello@iabf.dance.'
      } catch (e) {
        console.error(e)
        this.error = 'Could not finish payment. Please try again.'
      }
      this.busy = false
    },
    async finishPaidRegistration (paymentIntentId) {
      try {
        await fetch(cfBase() + '/confirmRegistrationDeposit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            enrollmentId: this.enrollmentId,
            paymentIntentId
          })
        })
      } catch (e) { /* webhook will activate if this misses */ }
      await this.markWaitlistIfOffered(this.enrollmentId)
      this.clearHoldTimer()
      this.stashSuccessAuth()
      this.clearDraft()
      this.teardownStripe()
      this.$router.push({
        path: '/register/success',
        query: this.successQuery(
          this.enrollmentId ? { enrollmentId: this.enrollmentId } : {}
        )
      })
    },
    async applyDepositCredit () {
      this.error = ''
      if (!this.ensureHoldAlive()) {
        return false
      }
      this.busy = true
      try {
        const payload = this.payload()
        if (this.stripeSetupCustomerId) {
          payload.stripeCustomerId = this.stripeSetupCustomerId
        }
        const callable = functions.httpsCallable('completeRegistrationWithDepositCredit')
        const result = await callable(payload)
        const data = (result && result.data) || {}
        const enrollmentId = data.enrollmentId || ''
        if (!enrollmentId) {
          this.error = 'Registration saved but we lost the confirmation. Email hello@iabf.dance.'
          this.busy = false
          return false
        }
        this.rememberEnrollment(enrollmentId)
        await this.markWaitlistIfOffered(enrollmentId)
        this.clearHoldTimer()
        this.holdId = null
        this.heldRoom = ''
        this.holdExpiresAt = null
        this.holdSecondsLeft = 0
        this.busy = false
        return true
      } catch (e) {
        console.error(e)
        const code = (e && e.code) || ''
        if (code === 'already-exists' || /already registered/i.test(String(e && e.message) || '')) {
          this.busy = false
          return true
        }
        const msg = (e && e.message) || ''
        this.error = msg.replace(/^[^:]+:\s*/, '') || 'Could not apply deposit credit. Please try paying the deposit instead.'
        this.busy = false
        return false
      }
    },
    async startEmbeddedCardSetup (enrollmentId) {
      this.cardSetupMode = true
      this.payPreparing = true
      this.stripeReady = false
      this.error = ''
      try {
        const res = await fetch(cfBase() + '/createCardSetupSession', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dancerId: enrollmentId || undefined,
            email: this.email.trim().toLowerCase(),
            name: `${this.firstName} ${this.lastName}`.trim(),
            embedded: true,
            successUrl: window.location.origin + '/register/success?' +
              new URLSearchParams(this.successQuery({
                credit: '1',
                card_setup: '1',
                enrollmentId: enrollmentId || ''
              })).toString(),
            cancelUrl: window.location.origin + '/register'
          })
        })
        const data = await res.json().catch(() => ({}))
        if (data.customerId) {
          this.stripeSetupCustomerId = data.customerId
        }
        if (data.clientSecret) {
          await this.mountStripeElement({
            clientSecret: data.clientSecret,
            publishableKey: data.publishableKey,
            mode: 'setup'
          })
          return
        }
        if (!enrollmentId && /dancerId/i.test(String(data.error || ''))) {
          const registered = await this.applyDepositCredit()
          if (registered && this.enrollmentId) {
            await this.startEmbeddedCardSetup(this.enrollmentId)
            return
          }
        }
        this.error = data.error || data.message || 'Could not open the card form. Please try again.'
        this.payPreparing = false
      } catch (e) {
        console.error(e)
        this.error = 'Could not open the card form. Please try again.'
        this.payPreparing = false
      }
    },
    async saveSetupCard () {
      this.error = ''
      if (!this._stripe || !this._stripeElements || this._stripeMode !== 'setup') {
        await this.startEmbeddedCardSetup(this.enrollmentId || null)
        return
      }
      this.busy = true
      try {
        const returnUrl = window.location.origin + '/register/success?' +
          new URLSearchParams(this.successQuery({
            credit: '1',
            card_setup: '1',
            enrollmentId: this.enrollmentId || ''
          })).toString()
        const result = await this._stripe.confirmSetup({
          elements: this._stripeElements,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required'
        })
        if (result.error) {
          this.error = result.error.message || 'That card did not save. Please try again.'
          this.busy = false
          return
        }
        const si = result.setupIntent
        if (si && si.status === 'succeeded') {
          if (!this.enrollmentId) {
            const registered = await this.applyDepositCredit()
            if (!registered) {
              return
            }
          }
          try {
            await fetch(cfBase() + '/confirmCardSetup', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ setupIntentId: si.id })
            })
          } catch (e) { /* webhook backup */ }
          this.finishWithoutCard(true)
          return
        }
        this.error = 'Your bank is still confirming. Give it a moment, then try again.'
      } catch (e) {
        console.error(e)
        this.error = 'Could not save that card. Please try again.'
      }
      this.busy = false
    },
    finishWithoutCard (cardSaved) {
      const enrollmentId = this.enrollmentId || ''
      this.stashSuccessAuth()
      this.clearDraft()
      this.teardownStripe()
      this.$router.push({
        path: '/register/success',
        query: this.successQuery({
          credit: '1',
          enrollmentId,
          card_setup: cardSaved === true ? '1' : '0'
        })
      })
    }
  }
}
</script>

<style scoped>
.register-page {
  min-height: 70vh;
  padding: 32px 20px 64px;
  background: var(--iabf-bg, #f7f6f4);
}

.register-col {
  max-width: 480px;
  margin: 0 auto;
}
.early-banner {
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(138, 149, 125, 0.15);
  color: var(--iabf-ink, #1c1b1f);
  font-size: 14px;
  line-height: 1.45;
}
.closed-card {
  padding: 24px 20px;
  margin-top: 12px;
}
.credit-card-note {
  margin: 0 0 16px;
  padding: 14px 16px;
  background: rgba(138, 149, 125, 0.1);
}
.hold-timer {
  margin: 0 0 14px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba(138, 149, 125, 0.18);
  font-size: 14px;
  color: var(--iabf-ink, #1c1b1f);
}
.hold-timer.urgent {
  background: rgba(181, 74, 74, 0.12);
  color: #8b2e2e;
}
.hold-countdown {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}
.room-meta {
  display: block;
  margin-top: 4px;
  font-size: 13px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.room-meta.sold {
  color: #b54a4a;
  font-weight: 700;
}
.room-meta.open {
  color: #2f6b4f;
}
.room-meta.reserved {
  color: #4a5d3a;
  font-weight: 700;
}
.iabf-choice.is-saved-for-you {
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  border-color: var(--iabf-primary, #8a957d);
  background: var(--iabf-bg-accent-soft, #eef1ea);
  box-shadow: 0 0 0 3px rgba(138, 149, 125, 0.35);
}
.iabf-choice-main {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}
.saved-for-you-badge {
  align-self: flex-start;
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--iabf-primary, #8a957d);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.2;
}
.saved-room-unlock {
  margin-top: 18px;
  padding-top: 8px;
}
.recognize-banner {
  margin: 0 0 14px;
  padding: 12px 16px;
  border-radius: 14px;
  background: rgba(138, 149, 125, 0.16);
  font-size: 15px;
  line-height: 1.45;
  color: var(--iabf-ink, #1c1b1f);
}
.recognize-banner p {
  margin: 0;
}
.recognize-banner p + p {
  margin-top: 4px;
}
.recognize-hello {
  font-weight: 700;
}
.total-remaining-banner,
.total-full-banner {
  margin: 0 0 14px;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
}
.total-remaining-banner {
  background: rgba(138, 149, 125, 0.14);
  color: var(--iabf-ink, #1c1b1f);
}
.total-full-banner {
  background: #fdeeee;
  color: #8b2e2e;
  border: 1.5px solid #f0c4c4;
}
.roommate-field {
  margin-top: 8px;
  margin-bottom: 4px;
}
.iabf-choice.is-sold-out {
  opacity: 0.55;
  cursor: not-allowed;
}
.hold-expired-card {
  cursor: pointer;
}
.just-taken-card {
  margin: 0 0 18px;
  padding: 16px 18px;
  border-radius: 14px;
  background: #fdeeee;
  border: 1.5px solid #f0c4c4;
  color: #5c2b2b;
}
.just-taken-title {
  margin: 0 0 8px;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 22px;
  font-weight: 400;
  color: #8b2e2e;
}
.just-taken-body {
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.5;
}
.just-taken-dismiss {
  width: auto;
}
.cottage-fallback-card {
  background: rgba(138, 149, 125, 0.16);
  border-color: rgba(138, 149, 125, 0.45);
  color: var(--iabf-ink, #1c1b1f);
}
.cottage-fallback-card .just-taken-title {
  color: var(--iabf-ink, #1c1b1f);
}
.cottage-fallback-card .iabf-btn + .iabf-btn {
  margin-top: 8px;
}

.step-label {
  margin: 12px 0 20px;
}

.mailing-block {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 18px 18px;
  background: var(--iabf-bg-elevated, #fff);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
}
.mailing-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.mailing-heading {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--iabf-ink, #1c1b1f);
}
.mailing-block .iabf-hint {
  margin: 0;
}
.mailing-block .envelope-hint {
  margin: 0 0 8px;
}
.addr-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
select.iabf-input {
  appearance: auto;
  cursor: pointer;
}
.addr-envelope {
  min-height: 112px;
}
.addr-row .iabf-field {
  margin-bottom: 0;
}
.optional-mark {
  font-weight: 400;
  color: var(--iabf-ink-faint, #8a8792);
  text-transform: none;
  letter-spacing: 0;
  font-size: 13px;
}
@media (max-width: 480px) {
  .addr-row {
    grid-template-columns: 1fr;
  }
}

.review-card {
  background: var(--iabf-bg-elevated, #fff);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  padding: 16px 18px;
  margin: 0 0 16px;
}
.review-name {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 6px;
}
.review-line,
.review-address {
  margin: 0 0 4px;
  font-size: 16px;
  line-height: 1.45;
}
.review-missing {
  margin: 0;
  font-size: 15px;
  font-style: italic;
  color: var(--iabf-ink-muted, #5c5a63);
}

.welcome-pill {
  display: inline-block;
  margin: 0 0 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--iabf-bg-soft, #eef0eb);
  color: var(--iabf-ink, #1c1b1f);
  font-size: 14px;
  font-weight: 700;
}

.error-banner {
  background: #fff;
  border: 1.5px solid var(--iabf-danger, #b54a4a);
  border-radius: 12px;
  padding: 12px 14px;
  color: var(--iabf-danger, #b54a4a);
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.4;
}
.error-banner--cta {
  margin: 16px 0 12px;
}

.terms-card h3 {
  margin: 16px 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
}
.terms-card p {
  margin: 0 0 10px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink, #1c1b1f);
}
.terms-all-link {
  margin: 16px 0 0;
}
.terms-all-link a {
  font-weight: 700;
}

.terms-check {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 16px;
  line-height: 1.4;
  color: var(--iabf-ink, #1c1b1f);
  cursor: pointer;
}

.terms-check input {
  margin-top: 4px;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.summary-card .iabf-body {
  margin: 0;
  font-size: 16px;
  color: var(--iabf-ink-muted, #5c5a63);
}

.reg-stripe-box {
  margin: 0 0 12px;
  padding: 16px 14px 8px;
  background: var(--iabf-bg-elevated, #fff);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  min-height: 88px;
}
.reg-stripe-lead {
  margin: 0 4px 14px;
}
.reg-stripe-lead-title {
  margin: 0 0 4px;
  font-size: 17px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
}
.reg-stripe-lead-body {
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: var(--iabf-ink-muted, #5c5a63);
}
.reg-stripe-box #reg-card-element.is-waiting {
  min-height: 48px;
}
</style>
